import { memo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Text, TextLink } from '@frontend/design-system';
import { useChartContext } from '../chart.provider';
import { cardStyles } from '../common-styles';
import { LeaderboardDataValue } from './types';

type Props = LeaderboardDataValue & {
  columnId: string;
  noBorder?: boolean;
  rowId: string;
  trackingIdBase?: string;
};

export const Card = memo(({ noBorder, clickTarget, columnId, onClick, rowId, trackingIdBase, ...value }: Props) => {
  const { formatter, primaryValue, secondaryValue } = value;
  const { isSmallMode, labels } = useChartContext();

  // Determine which element gets the click props based on the clickTarget.
  const isCardClickable = onClick && clickTarget === 'card';
  const isLabelClickable = onClick && clickTarget === 'label';

  const commonClickProps = !!onClick
    ? {
        onClick: () =>
          onClick({
            columnId,
            rowId,
            value,
          }),
        'data-trackingId': `${trackingIdBase}-click-${columnId}`,
      }
    : {};

  const cardClickProps = isCardClickable
    ? {
        ...commonClickProps,
        className: 'clickable',
        role: 'button',
        tabIndex: 0,
      }
    : {};

  const label = labels[columnId] || columnId;

  return (
    <div {...cardClickProps} css={[cardStyles({ isSmallMode, noBorder }), styles.dataCard({ noBorder })]}>
      <Text css={styles.mainText}>
        {formatter?.(primaryValue) ?? primaryValue}
        {secondaryValue ? (
          <Text as='span' color='subdued' weight='regular' size='medium'>
            /{formatter?.(secondaryValue) ?? secondaryValue}
          </Text>
        ) : null}
      </Text>
      {isLabelClickable ? (
        <TextLink {...commonClickProps} size='small' textAlign='left' css={styles.clickableText}>
          {label}
        </TextLink>
      ) : (
        <Text color='subdued' size='small'>
          {label}
        </Text>
      )}
    </div>
  );
});

Card.displayName = 'Card';

const styles = {
  dataCard: ({ noBorder }: { noBorder?: boolean }) => css`
    background-color: ${noBorder ? 'transparent' : theme.colors.white};
    gap: 0;
    padding: ${theme.spacing(1)};

    &.clickable {
      cursor: pointer;

      &:hover {
        background-color: ${theme.colors.neutral5};
      }
    }
  `,

  mainText: css`
    font-size: ${theme.font.size.h3};
    font-weight: ${theme.font.weight.bold};
    line-height: ${theme.spacing(3)};
  `,
  clickableText: css`
    white-space: nowrap;
  `,
};
